import { useEffect, useState } from "react";
import { NewUserForm, UserTable } from "../components/Auth";
import { TotpAdminResetCell } from "../components/Totp";
import "../components/Auth.css";
import { Alert, Loading } from "../components/Card";
import { authApi, authErrText } from "../lib/auth";
import type { Role, UserRow } from "../lib/auth";
import type { UserPatch } from "../components/Auth";
import type { ProjectName } from "../components/ProjectScope";
import { projectApi } from "../lib/project";
import { tx } from "../i18n";

/* ══ 설정 › 사용자(관리자만) — 공통 ④(Figma 92-215) ══
   거절(마지막 관리자 · 자기 자신 막기 · 같은 아이디)은 **서버 문장 그대로** 보인다 —
   화면이 같은 규칙을 다시 두지 않는다(`crates/api/src/accounts.rs`).
   2단계 정책은 「로그인 · 2단계」 탭이고, 기기를 잃은 사람의 해제는 펼친 줄 안에 있다. */
export function UsersSection({ meId }: { meId?: string }) {
  const [rows, setRows] = useState<UserRow[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowErr, setRowErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);
  const [projects, setProjects] = useState<ProjectName[]>([]);
  const [savedId, setSavedId] = useState<string | null>(null);

  function load() {
    authApi
      .users()
      .then((r) => {
        setRows(r);
        setLoadErr(null);
      })
      .catch((e) => setLoadErr(authErrText(e)));
  }
  useEffect(() => {
    load();
    // 프로젝트 이름은 범위 칸의 이름표일 뿐이다 — 못 읽으면 id 그대로 보인다(목록은 막지 않는다).
    projectApi
      .list()
      .then((p) => setProjects(p.map(({ id, name }) => ({ id, name }))))
      .catch(() => setProjects([]));
  }, []);

  async function update(id: string, b: UserPatch) {
    setBusyId(id);
    setRowErr(null);
    setSavedId(null);
    try {
      const u = await authApi.updateUser(id, b);
      setRows((rs) => rs?.map((r) => (r.id === u.id ? u : r)) ?? rs);
      if ("email" in b || "projects" in b) setSavedId(id);
    } catch (e) {
      setRowErr(authErrText(e));
    } finally {
      setBusyId(null);
    }
  }

  async function create(b: { username: string; displayName?: string; role: Role; password: string; email?: string }) {
    setCreating(true);
    setCreateErr(null);
    setCreated(null);
    try {
      const u = await authApi.createUser(b);
      setCreated(u.username);
      load();
    } catch (e) {
      setCreateErr(authErrText(e));
    } finally {
      setCreating(false);
    }
  }

  return (
    <>
      <h2>{tx("users.title")}</h2>
      <p className="muted">{tx("users.lead")}</p>
      {loadErr ? <Alert tone="error">{tx("users.loadFailed", { err: loadErr })}</Alert> : null}
      {rows ? (
        <UserTable
          rows={rows}
          meId={meId}
          openId={openId}
          busyId={busyId}
          error={rowErr}
          projects={projects}
          savedId={savedId}
          onToggle={(id) => {
            setRowErr(null);
            setOpenId((o) => (o === id ? null : id));
          }}
          onUpdate={update}
          extra={(u) =>
            u.totpEnabled ? (
              <TotpAdminResetCell
                onReset={async (name) => {
                  // 조각은 아이디를 받는다(사람이 그 이름을 직접 쳐서 확인하게) — 문은 id 로 연다.
                  const target = rows?.find((r) => r.username === name);
                  if (!target) throw new Error(tx("users.noSuch"));
                  await authApi.adminTotpReset(target.id);
                  load();
                }}
              />
            ) : null
          }
        />
      ) : !loadErr ? (
        <Loading text={tx("users.loading")} />
      ) : null}
      <div className="acctgrid">
        <NewUserForm busy={creating} error={createErr} doneName={created} onSubmit={create} />
      </div>
    </>
  );
}
